(() => {
  const SAMPLE_PRODUCT_ID = 11520113672533;
  const SAMPLE_VARIANT_IDS = new Set([
    54698856710485, 54698856743253, 54698856776021, 54698856808789, 54698856841557,
    54698856874325, 54698856907093, 54698856939861, 54698856972629, 54698857005397,
    54698857038165, 54698857070933, 54698857103701, 54698857136469, 54698857169237,
  ]);
  const MAX_SAMPLES = 3;
  const MAX_PER_VARIANT = 1;

  const state = {
    ready: false,
    cart: null,
    sampleCount: 0,
    variantCounts: new Map(),
    refreshPromise: null,
    refreshTimer: null,
    normalizing: false,
  };

  const toNumber = (value) => Number.parseInt(String(value ?? ''), 10);
  const isSampleVariant = (variantId) => SAMPLE_VARIANT_IDS.has(toNumber(variantId));
  const isSampleItem = (item) =>
    Number(item?.product_id) === SAMPLE_PRODUCT_ID || isSampleVariant(item?.variant_id ?? item?.id);

  const cartUrl = () => `${window.routes?.cart_url || '/cart'}.js`;
  const cartChangeUrl = () => `${window.routes?.cart_change_url || '/cart/change'}.js`;

  const rebuildCartState = (cart) => {
    state.cart = cart;
    state.sampleCount = 0;
    state.variantCounts = new Map();
    (cart?.items || []).forEach((item) => {
      if (!isSampleItem(item)) return;
      const variantId = toNumber(item.variant_id ?? item.id);
      const quantity = Number(item.quantity || 0);
      state.sampleCount += quantity;
      state.variantCounts.set(variantId, (state.variantCounts.get(variantId) || 0) + quantity);
    });
  };

  const variantCount = (variantId) => state.variantCounts.get(toNumber(variantId)) || 0;

  const dispatchState = () => {
    document.dispatchEvent(new CustomEvent('lt:samples:updated', {
      detail: { count: state.sampleCount, max: MAX_SAMPLES, maxPerVariant: MAX_PER_VARIANT, ready: state.ready },
    }));
  };

  const lockSampleQuantityControls = (root = document) => {
    root.querySelectorAll?.('input[data-quantity-variant-id]').forEach((input) => {
      if (!isSampleVariant(input.dataset.quantityVariantId)) return;

      if ((toNumber(input.value) || 0) > MAX_PER_VARIANT) {
        input.value = String(MAX_PER_VARIANT);
        input.setAttribute('value', String(MAX_PER_VARIANT));
      }

      input.max = String(MAX_PER_VARIANT);
      input.setAttribute('max', String(MAX_PER_VARIANT));
      input.readOnly = true;
      input.setAttribute('aria-readonly', 'true');
      input.dataset.ltSampleQuantityLocked = 'true';

      const quantity = input.closest('quantity-input');
      const minus = quantity?.querySelector('.quantity__button[name="minus"], button[name="minus"]');
      const plus = quantity?.querySelector('.quantity__button[name="plus"], button[name="plus"]');
      if (minus) minus.remove();
      if (plus) plus.remove();
    });
  };

  const renderControls = (root = document) => {
    lockSampleQuantityControls(root);
    dispatchState();
  };

  const publishCartUpdate = (cart) => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lt-sample-limit', cartData: cart });
  };

  const normalizeVariantQuantities = async (cart) => {
    if (state.normalizing) return cart;

    const invalidItems = (cart?.items || []).filter(
      (item) => isSampleItem(item) && Number(item.quantity || 0) > MAX_PER_VARIANT,
    );
    if (!invalidItems.length) return cart;

    state.normalizing = true;
    let latestCart = cart;

    try {
      for (const item of invalidItems) {
        const response = await fetch(cartChangeUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: MAX_PER_VARIANT }),
        });
        if (!response.ok) throw new Error(`Cart change HTTP ${response.status}`);
        latestCart = await response.json();
      }

      rebuildCartState(latestCart);
      state.ready = true;
      renderControls();
      publishCartUpdate(latestCart);
      return latestCart;
    } catch (error) {
      console.warn('Lačne Tačke – normalizacija količine vzorca:', error);
      return cart;
    } finally {
      state.normalizing = false;
    }
  };

  const refresh = async () => {
    if (state.refreshPromise) return state.refreshPromise;
    state.refreshPromise = fetch(cartUrl(), {
      credentials: 'same-origin', headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart HTTP ${response.status}`);
        return response.json();
      })
      .then(async (cart) => {
        rebuildCartState(cart);
        state.ready = true;
        const normalizedCart = await normalizeVariantQuantities(cart);
        rebuildCartState(normalizedCart);
        renderControls();
        return normalizedCart;
      })
      .catch((error) => {
        console.warn('Lačne Tačke – omejitev vzorcev:', error);
        return null;
      })
      .finally(() => { state.refreshPromise = null; });
    return state.refreshPromise;
  };

  const scheduleRefresh = (delay = 500) => {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(refresh, delay);
  };

  const getFormQuantity = (form) => {
    const quantity = toNumber(new FormData(form).get('quantity'));
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const canAdd = (variantId, quantity) =>
    variantCount(variantId) + quantity <= MAX_PER_VARIANT &&
    state.sampleCount + quantity <= MAX_SAMPLES;

  const resumeSubmit = (form, submitter) => {
    form.dataset.ltSampleLimitBypass = 'true';
    try {
      if (submitter && submitter.form === form) form.requestSubmit(submitter);
      else form.requestSubmit();
    } catch {
      form.submit();
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.ltSampleLimitBypass === 'true') {
      delete form.dataset.ltSampleLimitBypass;
      return;
    }

    const variantId = toNumber(new FormData(form).get('id'));
    if (!isSampleVariant(variantId)) return;
    const quantity = getFormQuantity(form);
    const submitter = event.submitter;

    const proceed = () => {
      if (!canAdd(variantId, quantity)) return;
      state.sampleCount += quantity;
      state.variantCounts.set(variantId, variantCount(variantId) + quantity);
      renderControls();
      resumeSubmit(form, submitter);
      scheduleRefresh(900);
    };

    event.preventDefault();
    event.stopImmediatePropagation();
    if (!state.ready) {
      refresh().then(() => {
        if (!state.ready) return;
        proceed();
      });
      return;
    }
    proceed();
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target?.closest?.('.quantity__button[name="plus"], button[name="plus"]');
    if (!button) return;
    const input = button.closest('quantity-input')?.querySelector('input[data-quantity-variant-id]');
    if (!input || !isSampleVariant(input.dataset.quantityVariantId)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input[data-quantity-variant-id]')) return;
    const variantId = toNumber(input.dataset.quantityVariantId);
    if (!isSampleVariant(variantId)) return;

    if (input.dataset.ltSampleLimitBypass === 'true') {
      delete input.dataset.ltSampleLimitBypass;
      return;
    }

    const proposedQuantity = Math.max(0, toNumber(input.value) || 0);
    const validateChange = () => {
      const currentQuantity = variantCount(variantId);
      if (proposedQuantity > MAX_PER_VARIANT) {
        input.value = String(MAX_PER_VARIANT);
        input.setAttribute('value', String(MAX_PER_VARIANT));
        return false;
      }
      const desiredTotal = state.sampleCount - currentQuantity + proposedQuantity;
      if (desiredTotal > MAX_SAMPLES) {
        input.value = String(currentQuantity);
        return false;
      }
      state.sampleCount = desiredTotal;
      state.variantCounts.set(variantId, proposedQuantity);
      renderControls();
      scheduleRefresh(700);
      return true;
    };

    if (!state.ready) {
      event.preventDefault();
      event.stopImmediatePropagation();
      refresh().then(() => {
        if (!state.ready || !validateChange()) return;
        input.dataset.ltSampleLimitBypass = 'true';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      return;
    }

    if (!validateChange()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  const patchCartQuantityUpdates = (tagName) => {
    customElements.whenDefined(tagName).then(() => {
      const ElementClass = customElements.get(tagName);
      const prototype = ElementClass?.prototype;
      if (!prototype?.updateQuantity || prototype.updateQuantity.__ltSampleLimitPatched) return;

      const originalUpdateQuantity = prototype.updateQuantity;
      const patchedUpdateQuantity = function (line, quantity, event, name, variantId) {
        const quantityInput =
          this.querySelector?.(`#Quantity-${line}`) ||
          this.querySelector?.(`#Drawer-quantity-${line}`) ||
          this.querySelector?.(`input[data-index="${line}"]`);
        const resolvedVariantId = toNumber(variantId || quantityInput?.dataset.quantityVariantId);

        if (isSampleVariant(resolvedVariantId) && Number(quantity) > MAX_PER_VARIANT) {
          if (quantityInput) {
            quantityInput.value = String(MAX_PER_VARIANT);
            quantityInput.setAttribute('value', String(MAX_PER_VARIANT));
          }
          lockSampleQuantityControls(this);
          scheduleRefresh(50);
          return;
        }

        return originalUpdateQuantity.call(this, line, quantity, event, name, variantId);
      };

      patchedUpdateQuantity.__ltSampleLimitPatched = true;
      prototype.updateQuantity = patchedUpdateQuantity;
    }).catch(() => {});
  };

  let renderQueued = false;
  const observer = new MutationObserver(() => {
    if (renderQueued) return;
    renderQueued = true;
    window.requestAnimationFrame(() => {
      renderQueued = false;
      lockSampleQuantityControls();
    });
  });

  const start = () => {
    patchCartQuantityUpdates('cart-items');
    patchCartQuantityUpdates('cart-drawer-items');
    lockSampleQuantityControls();
    observer.observe(document.body, { childList: true, subtree: true });
    refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      if (PUB_SUB_EVENTS.cartUpdate) subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleRefresh(100));
      if (PUB_SUB_EVENTS.cartError) subscribe(PUB_SUB_EVENTS.cartError, () => scheduleRefresh(100));
    }
  };

  window.addEventListener('pageshow', () => scheduleRefresh(50));
  window.addEventListener('focus', () => scheduleRefresh(100));
  document.addEventListener('shopify:section:load', (event) => {
    lockSampleQuantityControls(event.target || document);
    scheduleRefresh(100);
  });

  window.ltSampleLimit = {
    max: MAX_SAMPLES,
    maxPerVariant: MAX_PER_VARIANT,
    get count() { return state.sampleCount; },
    get ready() { return state.ready; },
    refresh,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
